import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import './Salon.css';

const EditMemberInfoModal = ({ isOpen, onClose, salonId, member, canChangeRole, onSaved }) => {
  const { t } = useLanguage();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [position, setPosition] = useState('');
  const [role, setRole] = useState('member');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen && member) {
      setFirstName(member.first_name || '');
      setLastName(member.last_name || '');
      setPosition(member.position || '');
      setRole(member.role || 'member');
      setError('');
    }
  }, [isOpen, member]);

  if (!isOpen || !member) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError(t('settings.nameRequired'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const trimmedFirst = firstName.trim();
      const trimmedLast = lastName.trim();
      const trimmedPosition = position.trim();

      if (canChangeRole && role !== member.role) {
        try {
          await axios.put(`/api/salons/${salonId}/members/${member.id}/role`, { role }, config);
        } catch (err) {
          setError(err.response?.data?.error || t('settings.failedToUpdateRole'));
          setSaving(false);
          return;
        }
      }

      await axios.put(`/api/salons/${salonId}/members/${member.id}/info`, {
        firstName: trimmedFirst,
        lastName: trimmedLast,
        position: trimmedPosition
      }, config);
      onSaved(trimmedFirst, trimmedLast, trimmedPosition, canChangeRole ? role : member.role);
    } catch (err) {
      setError(err.response?.data?.error || t('settings.failedToUpdateName'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '420px' }}>
        <div className="modal-header">
          <h3>{t('settings.editMemberName')}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="edit_member_first_name">{t('auth.firstName')}</label>
              <input
                type="text"
                id="edit_member_first_name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                maxLength={100}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit_member_last_name">{t('auth.lastName')}</label>
              <input
                type="text"
                id="edit_member_last_name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit_member_position">{t('settings.position')}</label>
              <input
                type="text"
                id="edit_member_position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                maxLength={100}
                placeholder={t('settings.positionPlaceholder')}
              />
            </div>
            {canChangeRole && (
              <div className="form-group">
                <label htmlFor="edit_member_role">{t('salon.role')}</label>
                <select
                  id="edit_member_role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="member">{t('salon.member')}</option>
                  <option value="admin">{t('salon.admin')}</option>
                </select>
              </div>
            )}
            {error && <div className="error-message">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="salon-button secondary" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="salon-button primary" disabled={saving}>
              {saving ? `${t('common.save')}...` : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMemberInfoModal;
